<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import UIKit, { type ITableSortSettings, type ITableColumn, type TTableRow } from '@pocketprep/ui-kit'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import type { Study } from '@pocketprep/types'
import AddInstructorSidePanel from '@/components/SidePanels/AddInstructor.vue'
import EditInstructorSidePanel from '@/components/SidePanels/EditInstructor.vue'
import ArchivedInstructorSidePanel from '@/components/SidePanels/ArchivedInstructor.vue'
import { instructorModule } from '@/store/instructor/module'
import { studentModule } from '@/store/student/module'
import { examModule } from '@/store/exam/module'
import { groupModule } from '@/store/group/module'

const {
    Table,
    Button: PocketButton,
    Tooltip,
    Icon,
} = UIKit

interface IMappedInstructors extends TTableRow {
    id: string
    name: string
    students: number
    examPrep: string
    role: string
    styles?: { [key: string]: string }
    lastName: string
    status: string
    hideRowEdit: boolean
    examPrepTooltip: string
    editTooltipText: string
}

const activeTab = ref<'active' | 'archived'>('active')
const showAddInstructorSidePanel = ref(false)
const showEditInstructorSidePanel = ref(false)
const showArchivedInstructorSidePanel = ref(false)
const editInstructorId = ref<null | string>(null)
const editPendingInstructorId = ref<null | string>(null)
const editArchivedInstructorId = ref<null | string>(null)
const rowHoverId = ref<string | number | null>(null)
const cellHovered = ref<null | { propName: string; id: string }>(null)
const instructorColumns: ITableColumn[] = [{
    name: 'Instructor Name',
    propName: 'name',
    styles: {
        width: '200px',
        marginRight: '0',
    },
}, {
    name: '# Students',
    propName: 'students',
    styles: {
        width: '100px',
        justifyContent: 'center',
        textAlign: 'center',
    },
}, {
    name: 'Exam Prep',
    propName: 'examPrep',
    styles: {
        width: '216px',
    },
}, {
    name: 'Role',
    propName: 'role',
    styles: {
        width: '162px',
    },
}, {
    propName: 'edit',
    isLabelHidden: true,
    styles: {
        margin: '0 0 0 0',
        width: '24px',
    },
}]
const sortSettings = ref<ITableSortSettings>({ column: instructorColumns[0], direction: 1 })

const currentOrgInstructors = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructors()
})

const currentOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructor()
})

const currentOrgExams = computed(() => {
    return examModule.getters.getCurrentOrgExams()
})

const currentGroups = computed(() => {
    return groupModule.getters.getCurrentOrgGroups()
})

const mappedInstructors = computed(() => {
    const sortSettingsVal = sortSettings.value

    return Object.values(currentOrgInstructors.value || [])
        .reduce((acc, i) => {
            const user = i.user as Study.Class.UserJSON
            const name = user.firstName && user.lastName
                ? `${[ user.firstName, user.lastName ].join(' ')}<div class="email">${user.username}</div>`
                : user.username

            const role = i.isAdmin ? 'Admin' : 'Instructor'

            const instructorGroups = groupModule.getters.searchCurrentOrgGroups({
                type: 'system',
                instructorId: i.objectId,
            })

            const studentsCount = instructorGroups.reduce((acc2, g) =>
                new Set([
                    ...acc2,
                    ...(currentGroups.value[g.objectId]?.orgStudents.map(os => os.objectId) || []),
                ]),
            new Set()).size

            const instructorExamNames = [ ...new Set(instructorGroups.map(
                group => currentOrgExams.value[group.orgExam.objectId]?.displayName
            )) ]

            const examPrep = i.isAdmin
                ? `All (${Object.values(currentOrgExams.value).length})`
                : instructorExamNames?.join(', ')

            const examPrepTooltip = i.isAdmin
                ? Object.values(currentOrgExams.value).map(exam => exam.displayName).join('<br>')
                : instructorExamNames.length > 1 ? instructorExamNames.join('<br>') : ''

            const styles: { [key: string]: string } = {}
            if (i.status === 'pending') {
                styles.color = BrandColors.slate01 || ''
            }
            if (rowHoverId.value === i.objectId) {
                styles.backgroundColor = `${BrandColors.grayBackground}60`
            }

            acc.push({
                id: i.objectId,
                name,
                students: studentsCount,
                examPrep,
                role: (i.status === 'pending' ? 'Pending ' : '') + role,
                status: i.status,
                styles,
                lastName: user.lastName || user.username,
                editTooltipText: `Edit ${role}`,
                examPrepTooltip,
                hideRowEdit: !currentOrgInstructor.value?.isAdmin
                    && (i.status !== 'pending' || i.isAdmin),
            })

            return acc
        }, [] as IMappedInstructors[])
        .sort((a, b) => {
            if (sortSettingsVal?.column) {
                if (sortSettingsVal.column.name === 'Instructor Name') {
                    const aName = a.lastName.slice(1)
                    const bName = b.lastName.slice(1)
                    return sortSettingsVal.direction * aName.localeCompare(bName)
                }
                if (sortSettingsVal.column.name === '# Students') {
                    return sortSettingsVal.direction * (b.students - a.students)
                }
                if (sortSettingsVal.column.name === 'Exam Prep') {
                    return sortSettingsVal.direction * (a.examPrep).localeCompare(b.examPrep)
                }
                if (sortSettingsVal.column.name === 'Role') {
                    return sortSettingsVal.direction * (a.role).localeCompare(b.role)
                }
            }

            return -1
        })
})

const activeTabInstructors = computed(() => {
    return mappedInstructors.value.filter(i => {
        return (activeTab.value === 'archived' && i.status === 'archived')
            || (activeTab.value === 'active' && i.status !== 'archived')
    })
})

const mappedActiveInstructors = computed(() => {
    return mappedInstructors.value.filter(i => i.status !== 'archived')
})

const mappedArchivedInstructors = computed(() => {
    return mappedInstructors.value.filter(i => i.status === 'archived')
})

onMounted(async () => {
    await Promise.all([
        instructorModule.actions.fetchCurrentOrgInstructors(),
        studentModule.actions.fetchCurrentOrgStudents(),
        groupModule.actions.fetchCurrentOrgGroups(),
        examModule.actions.fetchCurrentOrgExams(),
        groupModule.actions.fetchGroupsActiveStudentIds(),
    ])
})

const editClicked = (instructorId: string) => {
    if (!currentOrgInstructors.value) {
        throw new Error('editClicked: No instructors found.')
    }

    const instructor = currentOrgInstructors.value[instructorId]

    if (instructor?.status === 'pending') {
        editPendingInstructorId.value = instructorId
        showAddInstructorSidePanel.value = true
    } else if (instructor?.status === 'archived') {
        editArchivedInstructorId.value = instructorId
        showArchivedInstructorSidePanel.value = true
    } else {
        editInstructorId.value = instructorId
        showEditInstructorSidePanel.value = true
    }
}

const updateSortSettings = (sortSettingsVal: ITableSortSettings) => {
    sortSettings.value = sortSettingsVal
}

const rowEnter = (row: TTableRow) => {
    rowHoverId.value = row.id
}

const rowLeave = (row: TTableRow) => {
    if (row.id === rowHoverId.value) {
        rowHoverId.value = null
    }
}

const hideCellTooltip = () => {
    cellHovered.value = null
}

const showCellTooltip = (propName: string, id: string) => {
    cellHovered.value = {
        propName,
        id,
    }
}

const changeTab = (tabName: 'active' | 'archived') => {
    activeTab.value = tabName
}
</script>

<template>
    <div class="instructors">
        <AddInstructorSidePanel
            v-if="showAddInstructorSidePanel"
            :instructor-id="editPendingInstructorId || ''"
            @close="() => { showAddInstructorSidePanel = false; editPendingInstructorId = null }"
        />
        <EditInstructorSidePanel
            v-if="showEditInstructorSidePanel"
            :instructor-id="editInstructorId || ''"
            @close="() => { showEditInstructorSidePanel = false; editInstructorId = null }"
        />
        <ArchivedInstructorSidePanel
            v-if="showArchivedInstructorSidePanel && editArchivedInstructorId"
            :instructor-id="editArchivedInstructorId"
            @close="() => { showArchivedInstructorSidePanel = false; editArchivedInstructorId = null }"
        />
        <div class="instructors__tabs">
            <div
                class="instructors__tab"
                :class="{
                    'instructors__tab--active': activeTab === 'active'
                }"
                @click="changeTab('active')"
            >
                Admin &amp; Instructors ({{ mappedActiveInstructors.length }})
            </div>
            <div
                class="instructors__tab"
                :class="{
                    'instructors__tab--active': activeTab === 'archived'
                }"
                @click="changeTab('archived')"
            >
                Archived ({{ mappedArchivedInstructors.length }})
            </div>
        </div>
        <Table
            :rows="activeTabInstructors"
            :columns="instructorColumns"
            :default-sort="sortSettings"
            :sort-toggle-order="[ 1, -1 ]"
            :show-header="false"
            class="instructors__table"
            :table-row-styles="{
                height: '63px',
                paddingRight: '0'
            }"
            :table-cell-styles="{
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
            }"
            :table-column-labels-styles="{
                paddingRight: '0'
            }"
            @rowEnter="rowEnter"
            @rowLeave="rowLeave"
            @sort="updateSortSettings"
        >
            <template #tableCellValue="{ row, column }">
                <div
                    v-if="column.propName === 'name' && row.status === 'pending'"
                    class="instructors__email"
                    v-html="row.name"
                />
                <div
                    v-else-if="column.propName === 'name' && row.status !== 'pending'"
                    class="instructors__name"
                    v-html="row.name"
                />
                <template v-else-if="column.propName === 'edit'">
                    <Tooltip
                        v-if="cellHovered
                            && (cellHovered.propName === column.propName && cellHovered.id === row.id)"
                        class="instructors__edit-tooltip"
                    >
                        {{ row.editTooltipText }}
                    </Tooltip>
                    <Icon
                        v-show="rowHoverId === row.id"
                        class="instructors__edit"
                        type="edit"
                        @mouseover="showCellTooltip(column.propName, row.id)"
                        @mouseout="hideCellTooltip"
                        @click="editClicked(row.id)"
                    />
                </template>
                <template v-else-if="column.propName === 'examPrep'">
                    <Tooltip
                        v-if="cellHovered
                            && (cellHovered.propName === column.propName && cellHovered.id === row.id)
                            && row.examPrepTooltip"
                        class="instructors__exam-prep-tooltip"
                        theme="rightalign"
                    >
                        <div v-html="row.examPrepTooltip" />
                    </Tooltip>
                    <div
                        class="instructors__exam-prep"
                        @mouseover="showCellTooltip(column.propName, row.id)"
                        @mouseout="hideCellTooltip"
                        v-html="row[column.propName]"
                    />
                </template>
                <div v-else v-html="row[column.propName]" />
            </template>
        </Table>
        <PocketButton
            v-if="activeTab === 'active'"
            type="secondary"
            @click="showAddInstructorSidePanel = true"
        >
            + Add Colleague
        </PocketButton>
    </div>
</template>

<style lang="scss" scoped>


.instructors {
    button {
        margin-top: 9px;
    }

    &__tabs {
        border-bottom: 2px solid $gray-divider;
        display: flex;
        font-size: 16px;
        line-height: 22px;
        font-weight: 500;
        margin-bottom: 25px;
    }

    &__tab {
        position: relative;
        padding: 6px 8px;
        cursor: pointer;
        user-select: none;
        margin-right: 9px;

        &--active {
            font-weight: 600;

            &::after {
                content: '';
                width: 100%;
                height: 2px;
                bottom: -2px;
                left: 0;
                position: absolute;
                background: $brand-blue;
            }
        }
    }

    &__exam-prep-tooltip {
        position: absolute;
        transform: translate(20px, -100%);
        top: 0;
    }

    &__edit-tooltip {
        position: absolute;
        transform: translate(0, -100%);
        left: 12px;
    }

    &__title {
        font-size: 16px;
        line-height: 22px;
        margin: 0 0 3px 11px;
        font-weight: 600;
    }

    &__name {
        max-width: 100%;
    }

    &__email {
        max-width: 100%;
        font-size: 13px;
        line-height: 20px;
        font-weight: 500;
        color: $slate-03;
    }

    &__exam-prep {
        text-overflow: ellipsis;
        overflow: hidden;
        display: block;
    }

    &__edit {
        cursor: pointer;
        height: 24px;
        color: $brand-blue;
    }
}
</style>
